import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { MenuItem, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { State, WorkflowInvoke } from '@sierralabs/workflow';

import { useDebounce } from '../../../../common/Debounce.hooks';
import { CreateSubTaskParams, TaskContext } from '../../../tasks/Task.machine';
import GroupSelector from '../../components/GroupSelector';
import { useFindWorkflows } from '../../Workflow.hooks';
import {
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin
} from '../ServicePlugin';
import { Group } from '../../../groups/Group.interface';
import TaskAssignDialog from '../../../tasks/dialogs/TaskAssignDialog';

const StyledConfiguratorDiv = styled.div`
  .field {
    margin: 12px 0;
  }
`;

const CreateSubTasksConfigurator: React.FC<ServiceConfiguratorProps> = ({
  onChange,
  ...props
}) => {
  const mountRef = useRef(true);
  const onChangeRef = useRef(onChange);
  const [invoke, setInvoke] = useState(props.invoke);
  const [numberOfTasks, setNumberOfTasks] = useState(
    +(invoke?.data?.numberOfTasks || 0)
  );
  const debouncedNumberOfTasks = useDebounce(numberOfTasks, 300);
  const { workflowList, findWorkflows } = useFindWorkflows();
  // get a list of all workflows for use in the workflow dropdown selector
  useEffect(() => {
    findWorkflows({ limit: 100 });
  }, [findWorkflows]);
  const data: any = useMemo(() => {
    const data = invoke.data || {};
    if (data?.workflowId) {
      data.workflow = { id: data.workflowId };
      delete data.workflowId;
    }
    if (data?.groupIds) {
      data.groups = data.groupIds.map((id: number) => ({ id }));
      delete data.groupIds;
    }
    return data;
  }, [invoke.data]);
  useEffect(() => {
    // don't set invoke on init
    if (mountRef.current) {
      mountRef.current = false;
    } else {
      setInvoke(invoke => {
        const updatedInvoke = { ...invoke };
        if (!updatedInvoke.data) updatedInvoke.data = {};
        const data: CreateSubTaskParams['data'] = updatedInvoke.data;
        data.numberOfTasks = debouncedNumberOfTasks;
        onChangeRef.current?.(updatedInvoke);
        return updatedInvoke;
      });
    }
  }, [debouncedNumberOfTasks]);
  return (
    <StyledConfiguratorDiv>
      <TextField
        className='field'
        variant='outlined'
        label='Dialog Title'
        size='small'
        fullWidth
        value={invoke?.data?.dialogTitle || ''}
        onChange={event => {
          data.dialogTitle = event.target.value;
          const updatedInvoke = { ...invoke, data };
          setInvoke(updatedInvoke);
          onChange?.(updatedInvoke);
        }}
      />
      <TextField
        className='field'
        variant='outlined'
        label='Dialog Message'
        size='small'
        fullWidth
        value={invoke?.data?.dialogMessage || ''}
        onChange={event => {
          data.dialogMessage = event.target.value;
          const updatedInvoke = { ...invoke, data };
          setInvoke(updatedInvoke);
          onChange?.(updatedInvoke);
        }}
      />

      {workflowList && (
        <Autocomplete
          fullWidth
          value={
            (invoke?.data?.workflow as
              | CreateSubTaskParams['data']['workflow']
              | undefined) || null
          }
          options={workflowList.data}
          getOptionLabel={option => option.name}
          getOptionSelected={(option, value) => option.id === value.id}
          onChange={(_, value) => {
            const workflowId = value?.id;
            const workflow = workflowId
              ? workflowList?.data.find(w => w.id === workflowId)
              : undefined;
            // allow clearing workflow field
            data.workflow = workflow && {
              id: workflow.id,
              name: workflow.name
            };

            const updatedInvoke = { ...invoke, data };
            setInvoke(updatedInvoke);
            onChange?.(updatedInvoke);
          }}
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              size='small'
              variant='outlined'
              className='field'
              label='SubTask Workflow'
              placeholder='Select the SubTask Workflow'
            />
          )}
        />
      )}

      <TextField
        select
        className='field'
        variant='outlined'
        label='Number of SubTasks'
        size='small'
        fullWidth
        value={numberOfTasks.toString()}
        onChange={event => {
          const value = parseInt(event.target.value);
          setNumberOfTasks(
            isNaN(value) || !isFinite(value) || value < 0 ? 0 : value
          );
          if (!data.groups) data.groups = [];
          data.groups.length = value;
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(count => (
          <MenuItem key={count} value={count}>
            {count}
          </MenuItem>
        ))}
      </TextField>
      {invoke?.data?.numberOfTasks &&
        [...Array(invoke?.data?.numberOfTasks)].map((_, index) => (
          <GroupSelector
            key={index}
            value={invoke.data.groups?.[index]?.id}
            onChange={(group: Group | null) => {
              if (!data.groups) data.groups = [];
              if (!group) {
                data.groups[index] = null;
              } else {
                const { id, name } = group;
                data.groups[index] = { id, name };
              }
              const updatedInvoke = { ...invoke, data };
              setInvoke(updatedInvoke);
              onChange?.(updatedInvoke);
            }}
          />
        ))}
    </StyledConfiguratorDiv>
  );
};

const StyledDisplayDiv = styled.div`
  font-size: 13px;
  padding-left: 4px;
  text-indent: -4px;
  code {
    background: #f4f5fa;
    padding: 2px 5px;
    border-radius: 3px;
  }
  b {
    padding: 0 5px 0 0;
  }
`;

const CreateSubTasksDisplay: React.FC<ServiceDisplayProps> = ({ invoke }) => {
  return (
    <StyledDisplayDiv>
      <div className='line'>
        <code>Sub Task Type</code>
        <b>=</b>
        <i>
          {invoke.data?.workflow?.name ||
            `Workflow ID: ${invoke.data?.workflowId}`}
        </i>
      </div>
      <div className='line'>
        <code>Number of Sub Tasks</code>
        <b>=</b>
        <i>{invoke.data?.numberOfTasks}</i>
      </div>
      {invoke.data?.groups?.map((group: any, index: number) => (
        <div className='line' key={index}>
          <code>Assign To</code>
          <b>=</b>
          <i>{group?.name || `Group ID: ${group?.id}`}</i>
        </div>
      ))}
      {invoke.data?.groupIds?.map((id: number) => (
        <div className='line' key={id}>
          <code>Assign To</code>
          <b>=</b>
          <i>Group ID: {id}</i>
        </div>
      ))}
    </StyledDisplayDiv>
  );
};

export const CreateSubTasksPlugin: ServicePlugin<
  CreateSubTaskParams[] | CreateSubTaskParams
> = {
  src: 'createSubTasks',
  name: 'Create Sub Tasks',
  configurator: CreateSubTasksConfigurator,
  display: CreateSubTasksDisplay,
  validate: (condition: Partial<WorkflowInvoke>) => {
    return true;
  },
  service: (state: State<TaskContext>, params, render) => {
    console.log('createSubTasks params', state, params);
    if (
      state.isSimulation ||
      state.context.skipServicePlugins?.includes(CreateSubTasksPlugin.src)
    ) {
      return true;
    }
    const data = (Array.isArray(params) ? params : [params])[0].data;
    return new Promise((resolve, reject) => {
      const groupIds = data.groupIds || data.groups.map(g => g.id);
      render?.(
        <TaskAssignDialog
          title={data?.dialogTitle || 'Create Subtasks'}
          message={
            data?.dialogMessage || 'Select users assigned to the subtasks.'
          }
          multiple
          taskId={state.context.task.id!}
          groupIds={groupIds}
          open={true}
          onCancel={() => {
            render?.(undefined);
            reject();
          }}
          handleAssign={subTaskUsers => {
            state.context.subTaskUserIds = subTaskUsers;
            render?.(undefined);
            resolve(true);
          }}
        />
      );
    });
  }
};
